import React, {useEffect, useMemo, useState} from 'react'
import ProductRegistrationOrganizationListStyle from 'pages/ProductRegistrationOrganization/ProductRegistrationOrganizationList/style'
import Table from '@material-ui/core/Table'
import {useTranslation} from 'react-i18next'
import Checkbox from '@material-ui/core/Checkbox'
import SearchBar from 'components/Forms/SearchBar'
import Pagination from 'components/molcules/Pagination'
import {
  makeStyles,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
} from '@material-ui/core'

import {
  LIST_COLUMN_TABLE,
  SELECT_ORGANIZATION,
} from 'pages/ProductRegistrationOrganization/constant'

import DialogConfirm from 'pages/ProductRegistrationOrganization/Component/DialogConfirm'

import {
  FetchListOrgCanSellProductRequest,
  IContentOrg,
  registerProductSaleTargetApi,
  unregisterProductSaleTargetApi,
} from 'api/productApi'
import useOrgCanRegister from 'features/org/useOrgCanRegister'
import TableLoading from 'components/Table/TableLoading'
import TableEmpty from 'components/Table/TableEmpty'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'

type TableColumn = {
  id: number
  orgName: string
  account: string
  representative: string
  activeProduct: string
  usePeriod: string
  remark: string
}

type SectionInstitutionProps = {
  goodsId: string
  name: string
  period: string | number
  listOrgRegistered: string[]
}

const useStyles = makeStyles(ProductRegistrationOrganizationListStyle)
const SectionInstitution = (props: SectionInstitutionProps) => {
  const classes = useStyles()
  const {t} = useTranslation()
  const {goodsId, name, period, listOrgRegistered} = props

  const {
    onSearch,
    loading,
    listOrgCanRegister,
    totalPages,
    query,
    error,
    resetQuerySearch,
    changeQuery,
  } = useOrgCanRegister()
  const {onOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const [institution, setInstitution] = useState<TableColumn[]>([])
  const [searchBy, setSearchBy] = useState<'orgName' | 'email'>('orgName')
  const [selectOrg, setSelectOrg] = useState<number[]>([])
  const [oldSelOrg, setOldSelOrg] = useState<number[]>([])
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [listOrgChange, setListOrgChange] = useState<{
    register: string[]
    unregister: string[]
  }>({
    register: [],
    unregister: [],
  })

  const emptyRows = institution.length === 0 ? 5 : 5 - institution.length

  const handleChangeCheckOrg = (selectId: number) => {
    if (selectOrg.includes(selectId)) {
      setSelectOrg(selectOrg.filter((id) => id !== selectId))
    } else {
      setSelectOrg([...selectOrg, selectId])
    }
  }
  const handleChangeCheckAllOrg = (isChecked: boolean) => {
    if (isChecked) {
      const allOrgIds = institution.map((org) => org.id)
      setSelectOrg(allOrgIds)
    } else {
      setSelectOrg([])
    }
  }

  const handleValue = (value: string) => {
    resetQuerySearch()
    changeQuery(searchBy, value)
  }

  const isChangedRegister = useMemo(() => {
    return JSON.stringify(selectOrg) !== JSON.stringify(oldSelOrg)
  }, [selectOrg, oldSelOrg])

  const convertDataOrgDisplay = (data: IContentOrg[]) => {
    return data.map((item) => ({
      id: item.org.id,
      orgName: item.org.name,
      account: item.chiefUser.email,
      representative: item.chiefUser.firstName + item.chiefUser.lastName,
      activeProduct: name,
      usePeriod: period as string,
      remark: listOrgRegistered.includes(String(item.org.id))
        ? t('IRegistrationComplete')
        : '',
    }))
  }

  const handleGetListOrganizationCanRegistration = async () => {
    try {
      const payloadData: FetchListOrgCanSellProductRequest = {
        goodsId,
        payload: {
          page: query.page || 0,
          size: query.size || 10,
          orgName: query.orgName,
          email: query.email,
        },
      }
      onSearch(payloadData)
    } catch (error) {
      console.log('error,', error)
    }
  }
  const handleConfirm = async () => {
    try {
      const addOrgIds = selectOrg.filter((id) => !oldSelOrg.includes(id))
      const removeOrgIds = oldSelOrg.filter((id) => !selectOrg.includes(id))
      if (removeOrgIds.length > 0) {
        await unregisterProductSaleTargetApi({
          goodsId,
          orgIds: removeOrgIds,
        })
      }

      if (addOrgIds.length > 0) {
        await registerProductSaleTargetApi({
          goodsId,
          orgIds: addOrgIds,
        })
      }
      setOldSelOrg(selectOrg)
      setInstitution((prevState) =>
        prevState.map((org) => ({
          ...org,
          remark: selectOrg.includes(org.id) ? t('IRegistrationComplete') : '',
        })),
      )
      setShowConfirm(false)
      onSuccessModalOpen(t('ISuccess'))
    } catch (error) {
      onOpen(error?.message || t('IError'))
    }
  }

  const onOpenConfirmDialog = () => {
    const addOrgIds = selectOrg.filter((id) => !oldSelOrg.includes(id))
    const removeOrgIds = oldSelOrg.filter((id) => !selectOrg.includes(id))
    const listNameRemove = institution
      .filter((org) => removeOrgIds.includes(org.id))
      .map((org) => org.orgName)

    const listNameAdd = institution
      .filter((org) => addOrgIds.includes(org.id))
      .map((org) => org.orgName)
    setListOrgChange({
      ...listOrgChange,
      register: listNameAdd,
      unregister: listNameRemove,
    })
    setShowConfirm(true)
  }

  useEffect(() => {
    if (goodsId?.length > 0) {
      resetQuerySearch()
    }
  }, [goodsId])

  useEffect(() => {
    if (goodsId.length > 0) {
      handleGetListOrganizationCanRegistration()
    }
  }, [query.page, goodsId, query.size])

  useEffect(() => {
    setSelectOrg(listOrgRegistered.map((id) => Number(id)))
    setOldSelOrg(listOrgRegistered.map((id) => Number(id)))
  }, [listOrgRegistered])

  useEffect(() => {
    setInstitution(convertDataOrgDisplay(listOrgCanRegister))
  }, [listOrgCanRegister])

  return (
    <div
      style={{display: goodsId.length > 0 ? 'block' : 'none'}}
      className={classes.sectionInstitution}
    >
      <div className={classes.detailProduct}>
        <div className={classes.cardTitle}>■ {t('ISelectInstitution')}</div>
        <div className={classes.toolBar}>
          <FormControl variant='outlined'>
            <Select
              value={searchBy}
              onChange={(event: React.ChangeEvent<{value: unknown}>) => {
                setSearchBy(event.target.value as 'orgName' | 'email')
              }}
              style={{height: '40px'}}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {SELECT_ORGANIZATION.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <SearchBar
            value={query[searchBy]}
            onChangeValue={handleValue}
            onSearchEvent={handleGetListOrganizationCanRegistration}
            disabledValue={false}
          />
          <div style={{marginLeft: 'auto'}}>
            <Button
              color='primary'
              disabled={!isChangedRegister}
              variant='contained'
              onClick={onOpenConfirmDialog}
            >
              {t('IRegisterCard')}
            </Button>
          </div>
        </div>

        <Table size='small' className={classes.table}>
          <TableHead className={classes.tableHeader}>
            {LIST_COLUMN_TABLE.map((item) => (
              <TableCell
                align='center'
                key={item.key}
                style={{
                  whiteSpace: 'pre',
                }}
              >
                {item.key === 'checkbox' ? (
                  <Checkbox
                    checked={
                      institution.length > 0 &&
                      institution.every((org) => selectOrg.includes(org.id))
                    }
                    onChange={(e) => {
                      handleChangeCheckAllOrg(e.target.checked)
                    }}
                  />
                ) : (
                  t(item.label)
                )}
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            {institution.map((row: TableColumn) => (
              <TableRow key={row.id} className={classes.rowCell}>
                {LIST_COLUMN_TABLE.map((col) => (
                  <TableCell key={col.key} align='center'>
                    {col.key === 'checkbox' ? (
                      <Checkbox
                        checked={selectOrg.includes(row.id)}
                        onChange={() => handleChangeCheckOrg(row.id)}
                      />
                    ) : (
                      row[col.key as keyof TableColumn]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableLoading loading={loading} emptyRows={emptyRows} />
            <TableEmpty
              emptyRows={emptyRows}
              loading={loading}
              error={error}
              isEmpty={institution.length === 0}
            />
          </TableBody>
        </Table>
        <Pagination
          totalPageCount={totalPages}
          currentPageIndex={query.page}
          itemCountPerPage={query.size}
          setCurrentPageIndex={(e) => {
            changeQuery('page', e)
          }}
          onItemCountPerPageChanged={(e) =>
            changeQuery('size', e.target.value as number)
          }
          loading={loading}
        />
      </div>
      <DialogConfirm
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        productName={name}
        listRegister={listOrgChange.register}
        listUnRegister={listOrgChange.unregister}
      />
    </div>
  )
}
export default SectionInstitution
